import { useMemo } from "react";
import { PermissionNode } from "../../../../../datasources/responses/adminRoles";

/**
 * usePermissionTree builds fast-lookup maps and helper functions from a nested tree of permissions.
 *
 * @param items — list of root PermissionNodes with potential nested children
 * @returns utilities for tree traversal and relationship lookups
 *
 * Example `items` input:
 * [
 *   {
 *     id: "companies",
 *     label: "Administration",
 *     children: [
 *       {
 *         id: "company",
 *         children: [
 *           { id: "company.view", children: [] },
 *           { id: "company.update", children: [] }
 *         ]
 *       }
 *     ]
 *   }
 * ]
 */

export const usePermissionTree = (items?: PermissionNode[]) => {
	const itemMap = new Map<string, PermissionNode>(); // ID -> node
	const childMap = new Map<string, string[]>(); // Parent ID -> [Child IDs]
	const parentMap = new Map<string, string>(); // Child ID -> Parent ID

	/**
	 * Recursively walks the permission tree and builds:
	 * - itemMap for node reference
	 * - parentMap for reverse lookup
	 * - childMap for hierarchical traversal
	 */
	const buildMaps = (node: PermissionNode, parentId?: string) => {
		// Current node
		itemMap.set(node.id, node);

		// If this node has a parent, map it for
		// reverse lookup (child -> parent)
		if (parentId) {
			parentMap.set(node.id, parentId);
		}

		// If this node has children, map them for
		// hierarchical traversal (parent -> children)
		if (node.children?.length) {
			const childIds = node.children.map((child) => child.id);
			childMap.set(node.id, childIds);

			// Recursively map all children
			node.children.forEach((child) => buildMaps(child, node.id));
		}
	};

	// Start building maps from root items
	items?.forEach((node) => buildMaps(node));

	// Helper functions to get all descendants or ancestors
	const getAllDescendants = (id: string): string[] => {
		const result: string[] = [];
		const stack = [id];

		// Use a stack to traverse descendants
		while (stack.length) {
			const current = stack.pop()!;
			const children = childMap.get(current) ?? [];
			result.push(...children);
			stack.push(...children);
		}
		return result;
	};

	// Helper function to get all ancestors of a given ID
	const getAllAncestors = (id: string): string[] => {
		const result: string[] = [];
		let current = id;

		// Traverse up the parent map to find all ancestors
		while (parentMap.has(current)) {
			const parent = parentMap.get(current)!;
			result.push(parent);
			current = parent;
		}
		return result;
	};

	// Memoize the return value to avoid unnecessary recalculations
	return useMemo(
		() => ({
			itemMap,
			childMap,
			parentMap,
			getAllDescendants,
			getAllAncestors,
		}),
		[items]
	);
};
